import {
  Collapse,
  CollapseProps,
  IconButton,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import clsx from 'clsx';
import {
  FC,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useElementSize } from '../../hooks';
import { WbCard, WbCardProps } from '../WbCard';
import { GroupProps } from './types';

export interface WbDisclosureProps extends WbCardProps {
  children?: ReactNode;
  renderDynamicContent?: (expanded: boolean) => ReactNode;
  headerInfo?: JSX.Element;
  footer?: JSX.Element;
  cardClassName?: string;
  expanded?: boolean;
  collapseProps?: CollapseProps;
  handleChange?: MouseEventHandler<HTMLButtonElement>;
  placeholderHeight?: string;
  titleId?: string;
  /**
   * Provide a specific string aria label to the expand button, if not defined the label for the expand button is inferred by the title content.
   * Use this when the content of the title is too complex to act as a concise label for the expand button and a a controlled text alternative is required
   */
  expandButtonAriaLabel?: string;
  buttonProps?: React.ComponentProps<typeof IconButton>;
  /** Set automatically by `WbDisclosureStack`; controls style when in a stack of WbDisclosure. */
  groupProps?: GroupProps;
}

const useStyles = makeStyles(theme => ({
  disclosureHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    height: '20px',
  },
  disclosureDetails: {
    backgroundColor: theme.palette.background.default,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0px 0px 0px',
  },
  card: {
    background: theme.palette.background.default,
  },
  footer: {
    textAlign: 'end',
    padding: '8px',
    background: theme.palette.background.default,
  },
  iconContainer: {
    padding: 0,
    marginLeft: 10,
    transform: 'rotate(0deg)',
    '&$expanded': {
      transform: 'rotate(90deg)',
    },
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.standard,
    }),
  },
  icon: {
    fontSize: '24px',
    padding: '2px',
    color: theme.palette.secondary.main,
  },
  expanded: {},
}));

const CollapseDynamicContent = ({
  expanded,
  children,
  placeholderHeight,
  collapseProps,
  regionId,
}: {
  expanded: boolean;
  children: ReactNode;
  placeholderHeight?: string;
  collapseProps?: CollapseProps;
  regionId: string;
  roundBottom: boolean;
  hasShadow: boolean;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const { clientHeight } = useElementSize(contentRef, 10);

  const [collapsedHeight, setCollapsedHeight] = useState(0);

  useEffect(() => {
    if (!expanded) setCollapsedHeight(clientHeight);
  }, [expanded, clientHeight]);

  return (
    <Collapse
      {...collapseProps}
      collapsedSize={
        !collapsedHeight && placeholderHeight
          ? placeholderHeight
          : collapsedHeight
      }
      in={expanded}
    >
      <div ref={contentRef} id={regionId}>
        {children}
      </div>
    </Collapse>
  );
};

export const WbDisclosure: FC<WbDisclosureProps> = ({
  title,
  children,
  footer,
  headerInfo,
  cardClassName,
  expanded: parentExpanded,
  handleChange: parentHandleChange,
  headerStyle,
  cardStyle,
  collapseProps,
  renderDynamicContent,
  placeholderHeight,
  titleId,
  expandButtonAriaLabel,
  groupProps,
  ...props
}) => {
  const classes = useStyles();

  const theme = useTheme();

  const status = groupProps?.status ?? 'single';
  const isFirst = status === 'single' || status === 'first';
  const isLast = status === 'single' || status === 'last';

  const [innerExpanded, setInnerExpanded] = useState(false);

  const innerOnChange = () => {
    setInnerExpanded(old => !old);
  };

  const expanded = parentExpanded ?? innerExpanded;
  const handleChange = parentHandleChange ?? innerOnChange;
  const hasShadow = expanded && !isLast;

  const disclosureRegionId = useId();
  const fallbackTitleId = useId();

  const usedId = titleId ?? fallbackTitleId;

  const isContentDynamic = Boolean(renderDynamicContent);

  const topBorderRadius = isFirst ? theme.spacing(0.5) : 0;
  const bottomBorderRadius = isLast ? theme.spacing(0.5) : 0;
  const headerBottomBorderRadius =
    isLast && !expanded && !isContentDynamic ? theme.spacing(0.5) : 0;

  return (
    <WbCard
      footer={footer}
      leading={
        <IconButton
          onClick={handleChange ?? innerOnChange}
          className={clsx(classes.iconContainer, expanded && classes.expanded)}
          size="small"
          aria-label={expandButtonAriaLabel}
          aria-labelledby={expandButtonAriaLabel ? undefined : usedId}
          aria-expanded={expanded}
          aria-controls={disclosureRegionId}
        >
          <KeyboardArrowRightIcon color="primary" className={classes.icon} />
        </IconButton>
      }
      titleId={usedId}
      title={title}
      icon={headerInfo}
      headerStyle={{
        backgroundColor: expanded
          ? theme.palette.background.paper
          : theme.palette.background.default,
        transition: theme.transitions.create('background-color', {
          duration: theme.transitions.duration.standard,
        }),
        borderBottom:
          expanded || isContentDynamic
            ? `1px solid ${theme.palette.grey[200]}`
            : 'none',
        borderTopLeftRadius: topBorderRadius,
        borderTopRightRadius: topBorderRadius,
        borderBottomLeftRadius: headerBottomBorderRadius,
        borderBottomRightRadius: headerBottomBorderRadius,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        ...headerStyle,
      }}
      headerContentStyle={{
        height: 'auto',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      }}
      cardStyle={{
        // Raise above the next sibling in the stack so the shadow isn't painted over by it; earlier
        // items need a higher z-index than later ones, since equal z-index ties resolve to DOM order
        position: 'relative',
        zIndex: expanded && !isLast ? groupProps?.zIndex : undefined,
        // Lift the card above the sibling below it while expanded, so the content doesn't blend into the next item
        boxShadow: expanded && !isLast ? theme.shadows[1] : 'none',
        // Top border only on the first item, otherwise it doubles up with the previous item's bottom border
        borderTop: isFirst
          ? `1px solid ${theme.palette.blueGrey[100]}`
          : 'none',
        borderLeft: `1px solid ${theme.palette.blueGrey[100]}`,
        borderRight: `1px solid ${theme.palette.blueGrey[100]}`,
        borderBottom: hasShadow
          ? 'none'
          : `1px solid ${
              isLast ? theme.palette.blueGrey[100] : theme.palette.grey[200]
            }`,
        borderTopLeftRadius: topBorderRadius,
        borderTopRightRadius: topBorderRadius,
        borderBottomLeftRadius: bottomBorderRadius,
        borderBottomRightRadius: bottomBorderRadius,
        ...cardStyle,
      }}
      {...props}
    >
      {renderDynamicContent ? (
        <CollapseDynamicContent
          placeholderHeight={placeholderHeight}
          expanded={expanded}
          regionId={disclosureRegionId}
          roundBottom={isLast}
          hasShadow={hasShadow}
        >
          {renderDynamicContent(expanded)}
        </CollapseDynamicContent>
      ) : (
        <Collapse {...collapseProps} in={expanded}>
          <div id={disclosureRegionId}>{children}</div>
        </Collapse>
      )}
    </WbCard>
  );
};
